import type { MarketingNavLink } from './content';
import type { MarketingLandingCopy } from '@/i18n/marketing-landing-copy';

const SOLUTIONS_HREF = '#solutions';
const RESOURCES_HREF = '#resources';

export type MobileNavItem =
  | {
      kind: 'section';
      id: string;
      title: string;
      accessibleTitle: string;
      links: Array<MarketingNavLink>;
    }
  | {
      kind: 'link';
      id: string;
      label: string;
      href: string;
    };

function getStableNavId(value: string): string {
  return value.replace(/^#|^\//, '').replaceAll('/', '-') || 'home';
}

function getSolutionItems(
  copy: MarketingLandingCopy,
  solutionsLabel: string
): Array<MobileNavItem> {
  return copy.solutionsGroups.map((group, index) => {
    const title = `${solutionsLabel} ${group.title.charAt(0).toLocaleLowerCase()}${group.title.slice(1)}`;

    return {
      kind: 'section',
      id: `solutions-${index}`,
      title,
      accessibleTitle: title,
      links: group.links,
    };
  });
}

function getResourcesItem(
  copy: MarketingLandingCopy,
  resourcesLabel: string
): MobileNavItem {
  return {
    kind: 'section',
    id: 'resources',
    title: resourcesLabel,
    accessibleTitle: resourcesLabel,
    links: copy.resourcesGroup.links,
  };
}

/**
 * Mobile preserves the desktop navigation order while splitting Solutions into
 * its role and industry groups. Each visible row names its specific group.
 */
export function getMobileNavItems(
  copy: MarketingLandingCopy
): Array<MobileNavItem> {
  return copy.primaryNav.flatMap((link): Array<MobileNavItem> => {
    if (link.href === SOLUTIONS_HREF) {
      return getSolutionItems(copy, link.label);
    }

    if (link.href === RESOURCES_HREF) {
      return [getResourcesItem(copy, link.label)];
    }

    return [
      {
        kind: 'link',
        id: getStableNavId(link.href),
        label: link.label,
        href: link.href,
      },
    ];
  });
}

export function getMobileNavActions(copy: MarketingLandingCopy) {
  return [
    {
      id: 'login',
      label: copy.brand.loginLabel,
      href: copy.brand.loginHref,
    },
    {
      id: 'start-creating',
      label: copy.brand.ctaLabel,
      href: copy.brand.ctaHref,
    },
  ];
}
